import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, Event } from '@angular/router';
import { Subscription, Observable, EMPTY, Subject, takeUntil } from 'rxjs';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaRemoteConfigService } from '@app/akita/api/remote-config/state/remote-config.service';
import { parseDeliveryAPIError } from '@app/akita/api/deliveries/models/delivery-info.model';
import { ErrorOverlayDynamicService } from '@app/overlays/error-overlay/services/error-overlay-dynamic.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaRouterService } from '@app/akita/router/state/router.service';
import { NgForm, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AkitaCheckoutService } from '@app/akita/api/checkout/state/checkout.service';
import { PaymentOrderModel } from '@app/akita/api/checkout/models/payment-order.model';
import { AkitaCheckoutQuery } from '@app/akita/api/checkout/state/checkout.query';
import { FooterComponent } from '../../../shared/components/new-footer/footer.component';
import { TrackOrderComponent } from '../../components/track-order/track-order.feature.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { A11yModule } from '@angular/cdk/a11y';

@Component({
  selector: 'app-order-tracker',
  templateUrl: './order-tracker.template.html',
  styleUrls: ['./order-tracker.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    CdkScrollable,
    NgIf,
    TrackOrderComponent,
    ReactiveFormsModule,
    FormsModule,
    FooterComponent,
    AsyncPipe,
    A11yModule,
  ],
})
export class OrderTrackerComponent implements OnInit, OnDestroy {
  public isBrowser: boolean;
  public rtlMode: boolean;
  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};
  public permanentLink: string;

  public showAuthOverlay: boolean;
  public showErrorOverlay: boolean;
  public fetchingDeliveries: boolean;
  public deliveryId: string;
  public thirdPartyId: string;
  public shortId: string;
  public postalCode: string;
  public phoneNumber: string;

  public isPublicDelivery: boolean;
  public showSearchPublicDelivery: boolean;

  public order$: Observable<PaymentOrderModel | null> = EMPTY;
  public fetchingOrder$: Observable<boolean> = EMPTY;
  public lessThan$: Observable<ScreenSizeInfoState> = EMPTY;
  public biggerThan$: Observable<ScreenSizeInfoState> = EMPTY;

  private readonly urlObserver: Subscription | null;
  private readonly langChangesObserver: Subscription | null;
  private fetchOrderObserver?: Subscription | null;
  public fetchingDelivery: boolean;
  private errorOverlaySubscription?: Subscription | null;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly meta: MetaService,
    private readonly translateService: TranslocoService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaRouterService: AkitaRouterService,
    private readonly errorOverlayDynamicService: ErrorOverlayDynamicService,
    private readonly akitaRemoteConfigService: AkitaRemoteConfigService,
    private readonly akitaCheckoutService: AkitaCheckoutService,
    private readonly akitaCheckoutQuery: AkitaCheckoutQuery
  ) {
    this.destroySubject = new Subject();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.deliveryId = '';
    this.thirdPartyId = '';
    this.shortId = '';
    this.postalCode = '';
    this.phoneNumber = '';
    this.fetchingDeliveries = false;
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.fetchingDelivery = false;

    this.isPublicDelivery = false;
    this.showSearchPublicDelivery = false;

    this.rtlMode = false;
    this.showAuthOverlay = false;
    this.showErrorOverlay = false;
    this.permanentLink = '';

    this.fetchingOrder$ = this.akitaCheckoutQuery.selectFetchingOrder();

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('title_delivery_details');
          this.meta.setTitle('title_delivery_details', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    this.akitaRemoteConfigService.getRemoteConfigAsync();

    // Listen to Navigation End events
    this.urlObserver = this.router.events.pipe(takeUntil(this.destroySubject)).subscribe({
      next: ($event: Event) => {
        if ($event instanceof NavigationEnd) {
          let route = this.activatedRoute.snapshot;
          while (route.firstChild) {
            route = route.firstChild;
          }
          this.onNavigationEnd(
            route.params || {},
            route.queryParams || {},
            route.data || {}
          );
        }
      },
    });
  }

  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.onNavigationEnd(route.params || {}, route.queryParams || {}, route.data || {});
  }

  public onNavigationEnd(params: Params, queryParams: Params, routeData: Params): void {
    this.permanentLink = WindowUtils.getLocationHref();
    this.params = params || {};
    this.queryParams = queryParams || {};
    this.routeData = routeData || {};
    this.rtlMode = routeData.rtl || false;

    this.postalCode = this.queryParams.zip || this.params.zip;
    const orderId = this.queryParams.id || this.params.id;
    if (orderId) {
      this.getOrderInfo(orderId);
    }
  }

  public getOrderInfo(orderId: string): void {
    this.zone.runOutsideAngular(() => {
      this.fetchOrderObserver = this.akitaCheckoutService
        .fetchOrder(orderId)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {
            this.zone.run(() => {
              this.order$ = this.akitaCheckoutQuery.selectOrder();
              this.changeDetector.markForCheck();
            });
          },
          error: (error: unknown) => {
            this.fetchingDelivery = false;

            const parsedError = parseDeliveryAPIError(error);
            this.openErrorOverlay(parsedError.error, parsedError.message, 'TTGDI');
          },
        });
    });
  }

  private openErrorOverlay(title: string, message: string, reason: string): boolean {
    if (!this.showErrorOverlay) {
      if (this.errorOverlaySubscription) {
        this.errorOverlaySubscription.unsubscribe();
      }

      this.errorOverlaySubscription = this.errorOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.showErrorOverlay = Boolean(data);
            if (!this.showErrorOverlay) {
              const fragment = this.akitaRouterQuery.homeUrlFragment;
              fragment.push('order');
              this.akitaRouterService.navigate(fragment, {});
            }

            this.changeDetector.markForCheck();
          },
        });

      this.errorOverlayDynamicService.open({
        data: {
          title: title,
          message: message,
          showReportButton: true,
          reason: `${reason || ''}`,
          extra: {
            'Error Title': this.translateService.translate(title),
            'Error Message': this.translateService.translate(message),
            'Delivery ID': this.deliveryId || '?',
          },
        },
        isBrowser: this.isBrowser,
      });
    }
    return false;
  }

  public onSubmitOrderId(form: NgForm) {
    if (form.value.delivery) {
      const fragment = this.akitaRouterQuery.homeUrlFragment;
      fragment.push('order');
      fragment.push(form.value.delivery);

      this.akitaRouterService.navigate(fragment);
      this.changeDetector.markForCheck();
    }
  }

  public scrollToTop(): void {
    try {
      if (
        this.akitaRouterQuery.isBrowser &&
        window.document &&
        window.document.getElementById
      ) {
        const scrollElement = window.document.getElementById('mainAppScroll');
        if (scrollElement) {
          if (scrollElement.scrollTo) {
            scrollElement.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          } else {
            scrollElement.scrollTop = 0;
          }
        }
      }
    } catch (err) {
      /* SSR Not Supported */
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.urlObserver) {
      this.urlObserver.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }

    if (this.fetchOrderObserver) {
      this.fetchOrderObserver.unsubscribe();
    }

    if (this.errorOverlaySubscription) {
      this.errorOverlaySubscription.unsubscribe();
    }
  }
}
